/*
 * DynamicReportBuilder — iter316
 * ---------------------------------------------------------------------------
 * Config-driven, extensible report builder that mounts inside
 * Settings → Report Configuration. Admins pick any registered data source
 * (Accounts, Properties, Contacts, Tasks, Audit History, Bookings, …),
 * choose which columns to include, layer filter conditions + a date range,
 * preview the row count, and export the filtered result to XLSX.
 *
 * Extensibility rule (matches backend):
 *   - Register a new source under `DATA_SOURCES` or `EXTERNAL_DATA_SOURCES`
 *     in `backend/routes/reports.py`
 *   - The `GET /reports/dynamic/schema` endpoint picks it up automatically
 *   - This component renders the new source with zero changes
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Loader2, Plus, Trash2, Download, Eye } from 'lucide-react';
import { toast } from 'sonner';

const API_BASE = process.env.REACT_APP_BACKEND_URL;
const authHeader = () => {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const emptyCondition = () => ({ field: '', operator: 'equals', value: '' });

const DynamicReportBuilder = () => {
  const [loading, setLoading] = useState(true);
  const [schema, setSchema] = useState(null);
  const [sourceKey, setSourceKey] = useState('');
  const [selectedFields, setSelectedFields] = useState([]);
  const [conditions, setConditions] = useState([]);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [preview, setPreview] = useState(null); // {count, sample_rows, fields}
  const [previewing, setPreviewing] = useState(false);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`${API_BASE}/api/reports/dynamic/schema`, { headers: authHeader() });
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const data = await r.json();
        setSchema(data);
        if (data.sources?.[0]) setSourceKey(data.sources[0].key);
      } catch (e) {
        toast.error('Failed to load report schema');
      } finally { setLoading(false); }
    })();
  }, []);

  const source = useMemo(
    () => (schema?.sources || []).find(s => s.key === sourceKey) || null,
    [schema, sourceKey]
  );

  // When the source changes, default-select every field and clear filters.
  useEffect(() => {
    if (!source) return;
    setSelectedFields(source.fields.map(f => f.key));
    setConditions([]);
    setPreview(null);
  }, [sourceKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleField = (key) => {
    setSelectedFields(prev => prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]);
    setPreview(null);
  };

  const addCondition = () => setConditions(prev => [...prev, emptyCondition()]);
  const updateCondition = (idx, patch) => setConditions(prev => prev.map((c, i) => i === idx ? { ...c, ...patch } : c));
  const removeCondition = (idx) => setConditions(prev => prev.filter((_, i) => i !== idx));

  const buildPayload = () => ({
    source: sourceKey,
    conditions: conditions.filter(c => c.field), // drop empty rows
    fields: selectedFields,
    start_date: startDate || null,
    end_date: endDate || null,
  });

  const runPreview = async () => {
    if (!sourceKey || selectedFields.length === 0) {
      toast.error('Pick a data source and at least one column'); return;
    }
    setPreviewing(true);
    try {
      const r = await fetch(`${API_BASE}/api/reports/dynamic/preview`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeader() },
        body: JSON.stringify(buildPayload()),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || `HTTP ${r.status}`);
      setPreview(data);
    } catch (e) {
      toast.error(e.message || 'Preview failed');
    } finally { setPreviewing(false); }
  };

  const runExport = async () => {
    if (!sourceKey || selectedFields.length === 0) {
      toast.error('Pick a data source and at least one column'); return;
    }
    setExporting(true);
    try {
      const r = await fetch(`${API_BASE}/api/reports/dynamic/export`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeader() },
        body: JSON.stringify(buildPayload()),
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err.detail || `HTTP ${r.status}`);
      }
      const blob = await r.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${sourceKey}_report.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => window.URL.revokeObjectURL(url), 5000);
      toast.success(`Exported ${r.headers.get('X-Row-Count') || 0} rows`);
    } catch (e) {
      toast.error(e.message || 'Export failed');
    } finally { setExporting(false); }
  };

  if (loading) return <div className="p-6 flex items-center gap-2 text-gray-500"><Loader2 className="animate-spin" size={16}/> Loading report schema…</div>;
  if (!schema) return <div className="p-4 bg-red-50 border border-red-200 text-red-700 rounded-xl text-sm">Failed to load report schema.</div>;

  const opsForField = (fieldKey) => {
    const field = source?.fields.find(f => f.key === fieldKey);
    return schema.operators?.[field?.type === 'number' ? 'number' : 'text'] || schema.operators.text;
  };

  return (
    <div className="border border-gray-200 rounded-xl p-5 bg-gradient-to-br from-white to-gray-50" data-testid="dynamic-report-builder">
      <div className="flex items-start justify-between mb-4">
        <div>
          <h4 className="text-base font-bold text-gray-800">Dynamic Report Builder</h4>
          <p className="text-xs text-gray-500 mt-0.5">Pick any CRM entity, filter, preview and export to Excel.</p>
        </div>
      </div>

      {/* Row 1: Source + Date range */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
        <div>
          <label className="block text-xs font-semibold text-gray-600 mb-1">Data Source</label>
          <select
            value={sourceKey}
            onChange={(e) => setSourceKey(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white focus:ring-2 focus:ring-teal-500 outline-none"
            data-testid="drb-source">
            {schema.sources.map(s => (
              <option key={s.key} value={s.key}>{s.label} ({s.kind})</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs font-semibold text-gray-600 mb-1">From Date</label>
          <input
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-teal-500 outline-none"
            max={endDate || undefined}
            data-testid="drb-start-date"/>
        </div>
        <div>
          <label className="block text-xs font-semibold text-gray-600 mb-1">To Date</label>
          <input
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-teal-500 outline-none"
            min={startDate || undefined}
            data-testid="drb-end-date"/>
        </div>
      </div>

      {/* Columns */}
      <div className="mb-4">
        <label className="block text-xs font-semibold text-gray-600 mb-1">Columns ({selectedFields.length}/{source?.fields.length || 0} selected)</label>
        <div className="flex flex-wrap gap-2 border border-gray-200 rounded-lg p-3 bg-white max-h-40 overflow-y-auto">
          {source?.fields.map(f => {
            const on = selectedFields.includes(f.key);
            return (
              <button key={f.key} type="button" onClick={() => toggleField(f.key)}
                data-testid={`drb-field-${f.key}`}
                className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${on ? 'bg-teal-100 border-teal-400 text-teal-800' : 'bg-white border-gray-300 text-gray-600 hover:bg-gray-50'}`}>
                {f.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Filters */}
      <div className="mb-4">
        <div className="flex items-center justify-between mb-1">
          <label className="text-xs font-semibold text-gray-600">Filters ({conditions.length})</label>
          <button type="button" onClick={addCondition} data-testid="drb-add-filter"
            className="text-xs text-teal-600 hover:text-teal-800 flex items-center gap-1"><Plus size={12}/> Add filter</button>
        </div>
        <div className="space-y-2">
          {conditions.length === 0 && (
            <div className="text-xs text-gray-400 italic px-2 py-1">No filters applied — the report will include all rows.</div>
          )}
          {conditions.map((c, idx) => (
            <div key={idx} className="grid grid-cols-12 gap-2 items-center bg-white border border-gray-200 rounded-lg p-2" data-testid={`drb-filter-row-${idx}`}>
              <select className="col-span-4 px-2 py-1.5 border border-gray-200 rounded-md text-xs bg-white"
                value={c.field}
                onChange={(e) => updateCondition(idx, { field: e.target.value, operator: 'equals', value: '' })}>
                <option value="">Pick field…</option>
                {source?.fields.map(f => <option key={f.key} value={f.key}>{f.label}</option>)}
              </select>
              <select className="col-span-3 px-2 py-1.5 border border-gray-200 rounded-md text-xs bg-white"
                value={c.operator}
                disabled={!c.field}
                onChange={(e) => updateCondition(idx, { operator: e.target.value })}>
                {opsForField(c.field).map(op => (<option key={op} value={op}>{op.replaceAll('_', ' ')}</option>))}
              </select>
              <input className="col-span-4 px-2 py-1.5 border border-gray-200 rounded-md text-xs"
                placeholder="Value"
                value={c.value}
                disabled={['is_empty', 'is_not_empty'].includes(c.operator)}
                onChange={(e) => updateCondition(idx, { value: e.target.value })}/>
              <button type="button" onClick={() => removeCondition(idx)}
                className="col-span-1 p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded" title="Remove filter">
                <Trash2 size={14}/>
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Actions */}
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <button type="button" onClick={runPreview} disabled={previewing}
          data-testid="drb-preview"
          className="px-4 py-2 bg-white border border-teal-500 text-teal-700 rounded-lg text-sm flex items-center gap-1.5 hover:bg-teal-50 disabled:opacity-60">
          {previewing ? <Loader2 size={14} className="animate-spin"/> : <Eye size={14}/>} Preview
        </button>
        <button type="button" onClick={runExport} disabled={exporting || !preview}
          data-testid="drb-export"
          className="px-4 py-2 bg-teal-600 text-white rounded-lg text-sm flex items-center gap-1.5 hover:bg-teal-700 disabled:opacity-60">
          {exporting ? <Loader2 size={14} className="animate-spin"/> : <Download size={14}/>} Export XLSX
        </button>
        {preview && (
          <div className="text-xs text-gray-600 ml-2">
            <b>{preview.count}</b> matching row{preview.count === 1 ? '' : 's'} • showing first {preview.sample_rows.length}
          </div>
        )}
      </div>

      {/* Preview Table */}
      {preview && preview.sample_rows.length > 0 && (
        <div className="border border-gray-200 rounded-lg overflow-auto max-h-96 bg-white" data-testid="drb-preview-table">
          <table className="w-full text-xs">
            <thead className="bg-gray-100 sticky top-0">
              <tr>{selectedFields.map(k => {
                const lbl = source?.fields.find(f => f.key === k)?.label || k;
                return <th key={k} className="px-3 py-2 text-left font-semibold text-gray-700 whitespace-nowrap">{lbl}</th>;
              })}</tr>
            </thead>
            <tbody>
              {preview.sample_rows.map((r, i) => (
                <tr key={i} className="odd:bg-white even:bg-gray-50 border-t border-gray-100">
                  {selectedFields.map(k => (
                    <td key={k} className="px-3 py-1.5 text-gray-700 whitespace-nowrap max-w-xs truncate" title={r[k] ?? ''}>
                      {r[k] === null || r[k] === undefined || r[k] === '' ? <span className="text-gray-300">—</span> : String(r[k])}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default DynamicReportBuilder;
